<template>
  <div
    :class="[
      'custom-dropdown',
      'bid-dropdown',
      props.dropdownCustomClass,
      { 'dropdown-open': state.isOpen },
      { 'direction-top': state.isInvertDropdown }
    ]"
    @click.self="toggleDropdown">
    <div class="current-item" @click="toggleDropdown">
      <span>{{ formatCurrency(state.selectedBid) }}</span>
    </div>

    <div class="hidden-list-wrapper" ref="listWrapper"></div>

    <div :class="['custom-scrollbar-wrapper', 'scroller-fast-bid', 'dropdown-list', { open: state.isOpen }]">
      <swiper
        v-if="state.isSwiperInit"
        :scrollbar="true"
        :navigation="false"
        :mousewheel="{ enabled: true, thresholdDelta: 0, thresholdTime: 0.1 }"
        :check-active-index="true"
        :slides-per-view="8"
        :slides-per-group="8"
        :speed="1"
        direction="vertical"
        type="selectBidDropdown"
        @update-active-slide-index="(i) => updateSlideIndex(i)"
        @swiper-init="initSwiper"
        ref="swiper">
        <swiper-slide
          v-for="variant in state.bidVariants"
          :key="variant"
          @click="selectItem(variant)"
          :class="['dropdown-item', { current: state.selectedBid === variant }]">
          {{ formatCurrency(variant) }}
        </swiper-slide>
      </swiper>
    </div>
  </div>

  <div class="modal-overlay" v-if="state.isOpen" @click="state.isOpen = false"></div>
</template>

<script setup lang="ts">
  import { debounce, isNumber } from "lodash";
  import { format_currency } from "@/utils/currencies";
  import { defineExpose, nextTick, onMounted, reactive, ref, watch } from "vue";
  import { ILot } from "@/models";
  import Swiper from "@/components/_partials/Swiper.vue";

  const emit = defineEmits(["update:selectedBid"]);

  const props = withDefaults(
    defineProps<{
      lotData: ILot;
      userBidProp: number | null;
      currentBidProp: number | null;
      bidsCountProp: number | null;
      dropdownCustomClass?: string;
      directionVerticalProp?: "bottom" | "top";
      isChangeDirection?: boolean;
    }>(),
    {
      directionVerticalProp: "bottom",
      isChangeDirection: true
    }
  );

  const state = reactive({
    lotId: props.lotData.id,
    bidVariants: props.lotData.bid_variants,
    selectedBid: 0,
    userBid: props.userBidProp,
    currentBid: props.currentBidProp,
    bidsCount: props.bidsCountProp,
    isOpen: false,
    isSwiperInit: false,
    isInvertDropdown: false,
    activeSlideIndex: null
  });

  const swiperCp = ref<unknown>(null);
  const listWrapper = ref<HTMLDivElement>(null);

  watch(
    () => state.selectedBid,
    (newValue) => {
      emit("update:selectedBid", newValue);
    }
  );

  watch(
      () => props.userBidProp,
      (newValue) => (state.userBid = newValue)
  );

  watch(
      () => state.userBid,
      () => {
        refreshBidVariants();
        state.selectedBid = state.bidVariants[0];
      }
  );

  watch(
    () => props.currentBidProp,
    (newValue) => (state.currentBid = newValue)
  );

  watch(
    () => state.currentBid,
    () => {
      refreshBidVariants();
      state.selectedBid = state.bidVariants[0];
    }
  );

  watch(
    () => props.bidsCountProp,
    (newValue) => (state.bidsCount = newValue)
  );

  watch(
    () => state.bidsCount,
    () => {
      refreshBidVariants();
      state.selectedBid = state.bidVariants[0];
    }
  );

  onMounted(() => {
    refreshBidVariants();
    state.selectedBid = state.bidVariants[0];

    state.isInvertDropdown = props.directionVerticalProp === "top";
  });

  const updateSlideIndex = debounce((index) => {
    if (index >= state.bidVariants.length - 32) {
      addLotBidVariants();
      filterBidVariants();
      swiperCp.value.update();
    }
  }, 200);

  const initSwiper = (sw) => {
    swiperCp.value = sw;
  };

  const refreshBidVariants = () => {
    const minSelectedBidValue = state.userBid ? Math.max(state.userBid, state.currentBid) : state.currentBid;

    let maxAvailableVariant = state.bidVariants[state.bidVariants.length - 1];
    while (minSelectedBidValue > maxAvailableVariant) {
      addLotBidVariants();
      maxAvailableVariant = state.bidVariants[state.bidVariants.length - 1];
    }

    filterBidVariants();
  };
  const filterBidVariants = () => {
    const bidOnStartingPrice = props.lotData.bid_on_starting_price ?? 0;
    const startingPrice = props.lotData.starting_price ?? 0;
    const currentBid = state.currentBid ?? 0;
    const bidsCount = state.bidsCount ?? 0;
    const userBid = state.userBid ?? 0;

    if (bidOnStartingPrice && currentBid === startingPrice && bidsCount === 0) {
      state.bidVariants = state.bidVariants.filter((item) => item >= currentBid && item > userBid);
    } else {
      state.bidVariants = state.bidVariants.filter((item) => item > currentBid && item > userBid);
    }
  };
  const toggleDropdown = () => {
    state.isOpen = !state.isOpen;

    if (state.isOpen && !state.isSwiperInit) {
      state.isSwiperInit = true;
    }

    if (props.isChangeDirection) {
      state.isInvertDropdown = true;
      // state.isInvertDropdown = !isElementXPercentInViewport(listWrapper.value, 100);
    }

    if (state.isOpen && state.bidVariants.length < 50) {
      addLotBidVariants();
      filterBidVariants();
    }
  };

  const selectItem = (item) => {
    state.selectedBid = item;
    state.isOpen = false;
  };

  const addLotBidVariants = () => {
    let newVariant = Math.max(...state.bidVariants);
    let infiniteStep = props.lotData.bid_infinite_step;

    for (let i = 0; i < 50; i++) {
      newVariant += infiniteStep;
      state.bidVariants.push(newVariant);
    }

    if (swiperCp.value?.activeIndex >= 0) {
      const swiperActiveIndex = swiperCp.value.activeIndex;
      nextTick(() => {
        swiperCp.value.slideTo(swiperActiveIndex);
      });
    }
  };

  const isElementXPercentInViewport = (el, percentVisible) => {
    let rect = el.getBoundingClientRect(),
      windowHeight = window.innerHeight || document.documentElement.clientHeight;

    return !(
      Math.floor(100 - ((rect.top >= 0 ? 0 : rect.top) / +-rect.height) * 100) < percentVisible ||
      Math.floor(100 - ((rect.bottom - windowHeight) / rect.height) * 100) < percentVisible
    );
  };
  const formatCurrency = (amount) => {
    return format_currency(amount);
  };

  defineExpose({
    selectItem
  });
</script>
